import { useState, useCallback } from "react";
import { UseAdmin } from "../UseAdmin";

import { useProducts } from "../../context/ProductsContext";
import { productsService } from "../../Api/Administracion/Productos";
import { useStores } from "../../context/StoresContext";
import { UseUser } from "../UseUser";

/**
 * Hook que gestiona operaciones de productos.
 */
export const useProductOperations = () => {
  const { resetProducts, updateProductById, addStoreToProduct, setPagination, setIsSearchMode } =
    useProducts();

  const { updateProductCount, updateStoreById } = useStores();

    const { brandfcNombre: brand ,branchFiSucursalId} = UseAdmin();

  //* Estados por operación
  const [fetchState, setFetchState] = useState({ loading: false, error: null });
  const [imageState, setImageState] = useState({ loading: false, error: null });
  const [associateState, setAssociateState] = useState({
    loading: false,
    error: null,
  });
  const [dissociateState, setDissociateState] = useState({
    loading: false,
    error: null,
  });

  //* SEARCH BY TERM
  const searchProducts = useCallback(
    async (term, { page = 0, size = 10 } = {}) => {
      if (!brand) {
        return { success: false, error: "Brand no disponible" };
      }

      if (branchFiSucursalId == null) {
        return { success: false, error: "branchFiSucursalId no disponible" };
      }

      if (!term) {
        return { success: false, error: "Term es requerido" };
      }

      setFetchState({ loading: true, error: null });

      try {
        const response = await productsService.search(
          { brand, ceco:branchFiSucursalId, term },
          {
            page,
            size,
          }
        );

        const { content = [], pageable, ...paginationMeta } = response;

        const paginationData = {
          pageNumber: pageable?.pageNumber ?? page,
          pageSize: pageable?.pageSize ?? size,
          totalPages: paginationMeta.totalPages ?? 0,
          totalElements: paginationMeta.totalElements ?? 0,
          first: paginationMeta.first ?? true,
          last: paginationMeta.last ?? false,
        };

        resetProducts(content, paginationData);
        setPagination(paginationData);
        setIsSearchMode(true);

        setFetchState({ loading: false, error: null });
        return { success: true, data: content, pagination: paginationData };
      } catch (err) {
        const errorMessage = err.message || "Error al obtener productos";
        setFetchState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, branchFiSucursalId, resetProducts, setPagination, setIsSearchMode]
  );

  //* FETCH ALL
  const fetchProducts = useCallback(
    async ({ page = 0, size = 10 } = {}) => {
      if (!brand) {
        return { success: false, error: "Brand no disponible" };
      }

      if (branchFiSucursalId == null) {
        return { success: false, error: "branchFiSucursalId no disponible" };
      }

      setFetchState({ loading: true, error: null });

      try {
        const response = await productsService.getAll(
            { brand, branchFiSucursalId },
          {
            page,
            size,
          }
        );

        const { content = [], pageable, ...paginationMeta } = response;

        const paginationData = {
          pageNumber: pageable?.pageNumber ?? page,
          pageSize: pageable?.pageSize ?? size,
          totalPages: paginationMeta.totalPages ?? 0,
          totalElements: paginationMeta.totalElements ?? 0,
          first: paginationMeta.first ?? true,
          last: paginationMeta.last ?? false,
        };

        resetProducts(content, paginationData);
        setPagination(paginationData);
        setIsSearchMode(false);

        setFetchState({ loading: false, error: null });
        return { success: true, data: content, pagination: paginationData };
      } catch (err) {
        const errorMessage = err.message || "Error al obtener productos";
        setFetchState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, branchFiSucursalId, resetProducts, setPagination, setIsSearchMode]
  );

  //* FETCH POR ID DE STORE
  const fetchProductsByStore = useCallback(
    async (storeId, { page = 0, size = 10 } = {}) => {
      if (!brand) {
        return { success: false, error: "Brand no disponible" };
      }

      if (branchFiSucursalId == null) {
        return { success: false, error: "branchFiSucursalId no disponible" };
      }

      if (!storeId) {
        return { success: false, error: "storeId es requerido" };
      }

      setFetchState({ loading: true, error: null });

      try {
        const response = await productsService.getByStoreId(
          { brand, branchFiSucursalId, storeId },
          {
            page,
            size,
          }
        );

        const { content = [], pageable, ...paginationMeta } = response;

        const paginationData = {
          pageNumber: pageable?.pageNumber ?? page,
          pageSize: pageable?.pageSize ?? size,
          totalPages: paginationMeta.totalPages ?? 0,
          totalElements: paginationMeta.totalElements ?? 0,
          first: paginationMeta.first ?? true,
          last: paginationMeta.last ?? false,
        };

        resetProducts(content, paginationData);
        setPagination(paginationData);
        setIsSearchMode(false);

        // Actualizar productCount del almacén
        updateStoreById(storeId, {
          productCount: paginationData.totalElements,
        });

        setFetchState({ loading: false, error: null });
        return { success: true, data: content, pagination: paginationData };
      } catch (err) {
        const errorMessage = err.message || `Error al obtener productos del almacén ${storeId}`;
        setFetchState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, branchFiSucursalId, resetProducts, setPagination, updateStoreById, setIsSearchMode]
  );

  //* UPDATE IMAGEN
  const updateProductImage = useCallback(
    async (productId, imagePayload) => {
      if (!productId || !imagePayload) {
        return {
          success: false,
          error: "productId e imagePayload son requeridos",
        };
      }

      const { fileName, base64, contentType } = imagePayload;

      if (!fileName || !base64 || !contentType) {
        return {
          success: false,
          error: "imagePayload debe incluir fileName, base64 y contentType válidos",
        };
      }

      setImageState({ loading: true, error: null });

      try {
        const updatedProduct = await productsService.updateImage(productId, brand, {
          fileName,
          base64,
          contentType,
        });

        updateProductById(productId, {
          imageUrl: updatedProduct.imageUrl,
        });

        setImageState({ loading: false, error: null });
        return { success: true, data: updatedProduct };
      } catch (err) {
        const errorMessage = err.message || "Error al actualizar imagen";
        setImageState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [brand, updateProductById]
  );

  //* DELETE IMAGEN
  const deleteProductImage = useCallback(
    async (productId) => {
      if (!productId) {
        return {
          success: false,
          error: "ProductId es requerido",
        };
      }

      setImageState({ loading: true, error: null });

      try {
        const deletedProduct = await productsService.deleteImage(productId);

        updateProductById(productId, deletedProduct);

        setImageState({ loading: false, error: null });
        return { success: true, data: deletedProduct };
      } catch (err) {
        const errorMessage = err.message || "Error al eliminar imagen";
        setImageState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [updateProductById]
  );

  //* ASSIGN
  const associateProductToStore = useCallback(
    async (productId, storeIds = []) => {
      if (!productId || !Array.isArray(storeIds) || storeIds.length === 0) {
        return {
          success: false,
          error: "productId y storeIds (array no vacío) son requeridos",
        };
      }

      setAssociateState({ loading: true, error: null });

      try {
        const res = await productsService.addToStore(productId, storeIds);

        addStoreToProduct(productId, res.almacenes);

        for (let store of res.almacenes) {
          updateProductCount(store.id, 1);
        }

        setAssociateState({ loading: false, error: null });
        return { success: true, data: productId, storeIds };
      } catch (err) {
        const errorMessage = err.message || "Error al asociar producto";
        setAssociateState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [addStoreToProduct, updateProductCount]
  );

  //* UNASSIGN
  const dissociateProductFromStore = useCallback(
    async (productId, storeId) => {
      if (!productId || !storeId) {
        return {
          success: false,
          error: "productId y storeId son requeridos",
        };
      }

      setDissociateState({ loading: true, error: null });

      try {
        const updatedProduct = await productsService.removeFromStore(productId, storeId);

        // Actualizar producto en el estado
        updateProductById(productId, updatedProduct);

        // Decrementar productCount del almacén
        updateProductCount(storeId, -1);

        setDissociateState({ loading: false, error: null });
        return { success: true, data: { productId, storeId } };
      } catch (err) {
        const errorMessage = err.message || "Error al desasociar producto";

        /*eslint-disable-next-line no-console*/
        console.error("🟥 Error al desasociar producto", err);

        setDissociateState({ loading: false, error: errorMessage });
        return { success: false, error: errorMessage, rolledBack: true };
      }
    },
    [updateProductById, updateProductCount]
  );

  //* RESET ERRORS
  const resetFetchError = useCallback(() => {
    setFetchState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetImageError = useCallback(() => {
    setImageState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetAssociateError = useCallback(() => {
    setAssociateState((prev) => ({ ...prev, error: null }));
  }, []);

  const resetDissociateError = useCallback(() => {
    setDissociateState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    // Operaciones
    operations: {
      searchProducts,
      fetchProducts,
      fetchProductsByStore,
      updateProductImage,
      deleteProductImage,
      associateProductToStore,
      dissociateProductFromStore,
    },

    // Estados por operación
    states: {
      fetchState,
      imageState,
      associateState,
      dissociateState,
    },

    // Reset de errores
    resetErrors: {
      resetFetchError,
      resetImageError,
      resetAssociateError,
      resetDissociateError,
    },
  };
};
